import React, { useEffect } from 'react';
import { User, Calendar, Phone, Copy, MessageSquare, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import CallHandler from '@/components/CallHandler';
import { usePhoneValidation } from '@/hooks/usePhoneValidation';

interface PatientDetailsProps {
  patientName: string;
  patientId?: string;
  patientClinicDataId?: number;
  emrId?: string;
  gender?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  age?: number;
  className?: string;
  // Additional props for phone actions
  onCopyPhone?: (phone: string) => void;
  onSendSMS?: (phone: string) => void;
  // Props needed for CallHandler
  clinicNumbers?: any[];
  clinicNumbersSources?: any[];
  patientClinicTimezone?: string;
  clinicName?: string;
  emergencyPhone?: string;
  emergencyContactName?: string;
  hccId?: number;
  // Styling variants
  callButtonVariant?: 'default' | 'neutral';
  hideCallButton?: boolean;
}

const PatientDetails: React.FC<PatientDetailsProps> = ({
  patientName,
  patientId,
  patientClinicDataId,
  emrId,
  gender,
  dateOfBirth,
  phoneNumber,
  age,
  className = "",
  onCopyPhone,
  onSendSMS,
  clinicNumbers,
  clinicNumbersSources,
  patientClinicTimezone,
  clinicName,
  emergencyPhone,
  emergencyContactName,
  hccId,
  callButtonVariant = 'default',
  hideCallButton = false
}) => {
  const formatDateOfBirth = (dob: string) => {
    const date = new Date(dob);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const calculateAge = (dob: string) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    
    return calculatedAge;
  };

  const {
    isPhoneValid, 
    isPhoneInvalid,
    formatPhoneNumber: formatPhone
  } = usePhoneValidation();

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Patient Name */}
      <div className="font-semibold text-foreground text-sm">
        {patientClinicDataId ? (
          <Link 
            to={`/patient-details/${patientClinicDataId}`}
            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
          >
            {patientName}
          </Link>
        ) : (
          patientName
        )}
      </div>
      
      {/* EMR ID and Gender */}
      {(emrId || patientId || gender) && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          <span>EMR - {emrId || patientId}</span>
          {gender && (
            <>
              <span className="text-muted-foreground/60">•</span>
              <span>{gender}</span>
            </>
          )}
        </div>
      )}
      
      {/* Date of Birth and Age */}
      {dateOfBirth && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3" />
          <span>DoB - {formatDateOfBirth(dateOfBirth)}</span>
          {(age || dateOfBirth) && (
            <>
              <span className="text-muted-foreground/60">•</span>
              <span>Age - {age || calculateAge(dateOfBirth)}</span>
            </>
          )}
        </div>
      )}
      
      {/* Phone Number */}
      {phoneNumber && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Phone className="h-3 w-3" />
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${isPhoneInvalid(phoneNumber) ? 'text-red-600' : ''}`}>
              Ph - {formatPhone(phoneNumber)}
            </span>
            
            {isPhoneInvalid(phoneNumber) && (
              <>
                <AlertTriangle className="h-3 w-3 text-red-600" />
                <Badge variant="destructive" className="text-xs px-1 py-0">
                  Number is Invalid
                </Badge>
              </>
            )}
            
            {isPhoneValid(phoneNumber) && (
              <div className="flex items-center gap-1 ml-1">
                {/* Call Handler */}
                {!hideCallButton && (
                  <CallHandler
                    phoneNumber={phoneNumber}
                    patientName={patientName}
                    patientId={patientClinicDataId}
                    alternatePhone={phoneNumber}
                    emergencyPhone={emergencyPhone}
                    emergencyContactName={emergencyContactName}
                    hccId={hccId}
                    clinicName={clinicName}
                    timezone={patientClinicTimezone || 'US/Eastern'}
                    clinicNumbers={clinicNumbers || []}
                    clinicNumbersSources={clinicNumbersSources}
                    mobilePhone={phoneNumber}
                    homePhone=""
                    defaultSelectedNumber={phoneNumber}
                    phoneType="mobile"
                    disabled={false}
                    callButtonVariant={callButtonVariant}
                  />
                )}
                
                {/* Copy Button */}
                {onCopyPhone && (
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={() => onCopyPhone(phoneNumber)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                )}
                
                {/* SMS Button */}
                {onSendSMS && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={() => onSendSMS(phoneNumber)}
                        >
                          <MessageSquare className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Send SMS</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;